import { useEffect, useMemo } from "react";
import { useAtom, useAtomValue, atom } from "jotai";
import { atomWithStorage, RESET } from "jotai/utils";
import { useOrder } from "services/orders/OrderGuard";
import { groupBy } from "lodash";

export type ChangedItems = {
  surveyId: string;
  changedItems: {
    [key: string]: number;
  };
};
export const changedItemsAtom = atomWithStorage<ChangedItems>("changed-items", {
  surveyId: "",
  changedItems: {},
});

export const useSurvey = () => {
  const order = useOrder();
  const primarySurvey = order?.primarySurvey;
  const pendingSurvey = order?.pendingSurvey;
  const survey = pendingSurvey ?? primarySurvey;
  const [changedItems, setChangedItemsAtom] = useAtom(changedItemsAtom);
  const surveyId = survey?.id;
  useEffect(() => {
    if (changedItems.surveyId.length === 0 && surveyId) {
      setChangedItemsAtom((state) => ({
        ...state,
        surveyId,
      }));
    } else if (changedItems.surveyId !== surveyId) {
      setChangedItemsAtom(RESET);
    }
  }, [surveyId, setChangedItemsAtom]);

  return survey;
};

export const useSurveyRooms = () => {
  const survey = useSurvey();
  if (!survey) {
    return undefined;
  }

  const rooms = groupBy(survey.items, ({ room }) => room.id);
  return rooms;
};

export const useSurveyOrders = () => {
  const survey = useSurvey();
  if (!survey) {
    return {};
  }

  const orders = groupBy(survey.segments, ({ order: { number } }) => number);

  return orders;
};

export const useOrdersRoomSurveyItem = (
  roomId: string,
  orderNumber: string
) => {
  const orders = useSurveyOrders();

  if (!orders) {
    return [];
  }

  const rooms = groupBy(
    orders[orderNumber].flatMap((segment) => segment.items),
    (item) => item.roomId
  );

  return rooms?.[roomId];
};

export const useSurveyRoom = (roomId: string) => {
  const rooms = useSurveyRooms();

  return rooms?.[roomId];
};

export const useSurveyItem = (itemId: string) => {
  const survey = useSurvey();
  return survey?.items?.find(({ id }) => id === itemId);
};

export function getChangedItemQuantity(
  changedItems: Record<string, number>,
  orderNumber: string,
  roomId: string,
  itemId: string
) {
  return changedItems[`${orderNumber}:${roomId}:${itemId}`] ?? 0;
}
export const useChangedItemsAtom = () => useAtom(changedItemsAtom);
export const useGetChangedItemQuantityAtom = (
  roomId: string,
  itemId: string,
  orderNumber: string
) => {
  const key = `${orderNumber}:${roomId}:${itemId}`;
  return useMemo(
    () =>
      atom(
        (get) => get(changedItemsAtom).changedItems[key] ?? 0,
        (get, set, newQuantity: number) => {
          const currentState = get(changedItemsAtom);
          set(changedItemsAtom, {
            surveyId: currentState.surveyId,
            changedItems: {
              ...currentState.changedItems,
              [key]: newQuantity,
            },
          });
        }
      ),
    [key]
  );
};
export const useChangedItemQuantityAtom = (
  roomId: string,
  itemId: string,
  orderNumber: string
) => useAtom(useGetChangedItemQuantityAtom(roomId, itemId, orderNumber));

export const totalChangedItemsAtom = atom((get) => {
  const changedItems = get(changedItemsAtom);
  return Object.values(changedItems.changedItems).reduce<number>(
    (removedItems, num) => removedItems + Math.abs(num),
    0
  );
});
export const useTotalChangedItems = () => useAtomValue(totalChangedItemsAtom);
